import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  Avatar,
  Typography,
  Chip,
  Divider
} from '@mui/material';
import { mockUsers } from '../../data/mockData';
import { extendedMockProjects } from '../../data/projectsData';

interface RequestMentorModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: any) => void;
  preselectedMentorId?: string;
}

const RequestMentorModal: React.FC<RequestMentorModalProps> = ({ 
  open, 
  onClose, 
  onSubmit,
  preselectedMentorId 
}) => {
  const [formData, setFormData] = useState({
    mentorId: preselectedMentorId || '',
    projectId: '',
    message: ''
  });
  const [error, setError] = useState('');

  const mentors = mockUsers.filter(u => u.role === 'Mentor');
  const projects = extendedMockProjects;
  const selectedMentor = mentors.find(m => m.id === formData.mentorId);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError('');
  };

  const handleSubmit = () => {
    // Validaciones
    if (!formData.mentorId) {
      setError('Debes seleccionar un mentor');
      return;
    }
    if (!formData.projectId) {
      setError('Debes seleccionar un proyecto');
      return;
    }
    if (!formData.message.trim()) {
      setError('Debes escribir un mensaje explicando por qué necesitas mentoría');
      return;
    }
    if (formData.message.trim().length < 20) {
      setError('El mensaje debe tener al menos 20 caracteres');
      return;
    }

    // Crear solicitud
    const newRequest = {
      id: `req_${Date.now()}`,
      entrepreneurId: 'user_emprendedor_1', // Usuario actual
      mentorId: formData.mentorId,
      projectId: formData.projectId,
      message: formData.message,
      status: 'pending' as const,
      createdAt: new Date().toISOString()
    };

    onSubmit(newRequest);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      mentorId: preselectedMentorId || '',
      projectId: '',
      message: ''
    });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Solicitar Mentoría</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          
          <TextField
            select
            label="Selecciona un Mentor"
            value={formData.mentorId}
            onChange={handleChange('mentorId')}
            fullWidth
            required
          >
            {mentors.map(mentor => (
              <MenuItem key={mentor.id} value={mentor.id}>
                {mentor.name} - {mentor.expertise}
              </MenuItem>
            ))}
          </TextField>

          {selectedMentor && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  {selectedMentor.avatar}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2">{selectedMentor.name}</Typography>
                  <Chip label={selectedMentor.expertise} size="small" color="success" />
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
              {selectedMentor.industry && (
                <Typography variant="caption" display="block">
                  <strong>Industria:</strong> {selectedMentor.industry}
                </Typography>
              )}
              {selectedMentor.availability && (
                <Typography variant="caption" display="block">
                  <strong>Disponibilidad:</strong> {selectedMentor.availability}
                </Typography>
              )}
              {selectedMentor.rate && (
                <Typography variant="caption" display="block">
                  <strong>Tarifa:</strong> ${selectedMentor.rate}/hora
                </Typography>
              )}
            </Box>
          )}

          <TextField
            select
            label="Proyecto"
            value={formData.projectId}
            onChange={handleChange('projectId')}
            fullWidth
            required
            helperText="Selecciona el proyecto para el cual necesitas mentoría"
          >
            {projects.map(project => (
              <MenuItem key={project.id} value={project.id}>
                {project.name} ({project.stage})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Mensaje"
            value={formData.message}
            onChange={handleChange('message')}
            fullWidth
            required
            multiline
            rows={4}
            placeholder="Explica por qué necesitas mentoría y qué esperas lograr con ella..."
            helperText={`${formData.message.length} caracteres (mínimo 20)`}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained">
          Enviar Solicitud
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestMentorModal;
